import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

/**
 * Tastatur im Chat (Oktober 2026). Ziel: Das Eingabefeld faehrt MIT der
 * Tastatur hoch, ohne Luecke darunter, wie bei Signal und WhatsApp.
 *
 * Drei Wege, weil jede Umgebung die Tastatur anders meldet:
 *
 * - iOS-App: Das Tastatur-Plugin verkleinert die WebView mit resize: 'native'
 *   erst Animationsdauer + 0,2 s NACH keyboardWillShow (Keyboard.m,
 *   setKeyboardHeight:delay:). Das Feld stand so eine knappe Sekunde hinter der
 *   Tastatur und sprang dann hoch. Im Chat schalten wir deshalb auf
 *   resize 'none' und schieben den Inhalt selbst hoch, sobald keyboardWillShow
 *   die Hoehe meldet: per CSS-Uebergang in derselben Zeit wie die Tastatur.
 *   Beim Verlassen kommt 'native' zurueck, alle anderen Seiten bleiben, wie sie
 *   sind.
 * - Android-App: Die WebView wird nativ verkleinert (MainActivity sorgt seit
 *   Oktober 2026 dafuer, dass sie dabei genau an der Tastatur endet). Hier
 *   reicht es zu wissen, OB die Tastatur offen ist, damit die Eingabeleiste
 *   ihren Abstand fuer die Gestenleiste weglaesst.
 * - Browser/PWA: Weder iOS-Safari noch Chrome (Standard "resizes-visual")
 *   verkleinern das Layout. Sichtbar ist nur noch der "visual viewport". Der
 *   Chat nimmt dann genau dessen Hoehe und Lage an.
 *
 * Rueckgabe:
 * - open:     Tastatur offen (Eingabeleiste ohne Safe-Area-Abstand)
 * - inset:    px, um die der Chat unten Platz machen muss (nur iOS-App)
 * - animate:  inset in Bewegung uebergehen lassen (nur iOS-App)
 * - viewport: { height, top } des sichtbaren Bereichs (nur Browser, nur offen)
 */

/** Ab dieser Differenz zum Layout-Viewport gilt die Tastatur als offen (px).
 *  Darunter: ein- und ausfahrende Browserleisten. */
export const KEYBOARD_MIN_HEIGHT = 120;

/** Liest den sichtbaren Bereich im Browser. null = Tastatur zu oder unbekannt. */
export const readVisualViewport = (win) => {
  const vv = win && win.visualViewport;
  if (!vv) return null;
  // Mit zwei Fingern hineingezoomt: Der sichtbare Bereich ist dann auch kleiner,
  // das ist aber keine Tastatur — der Chat soll nicht mitschrumpfen.
  if (typeof vv.scale === 'number' && vv.scale > 1.05) return null;
  const covered = win.innerHeight - vv.height;
  if (covered < KEYBOARD_MIN_HEIGHT) return null;
  return { height: Math.round(vv.height), top: Math.max(0, Math.round(vv.offsetTop)) };
};

const CLOSED = { open: false, inset: 0, animate: false, viewport: null };

// Wie viele Chats gerade den Resize-Modus 'none' halten (iOS-App).
let activeChats = 0;

/**
 * Beim App-Start aufrufen (App.js): stellt auf iOS resize 'native' sicher.
 * Laedt die App aus dem Chat heraus hart neu (abgelaufene Anmeldung,
 * Fehlerseite, Chunk-Reload), laeuft das Aufraeumen des Chats nicht — der
 * native Modus bliebe bis zum App-Neustart auf 'none', und auf allen anderen
 * Seiten laegen Felder und Knoepfe hinter der Tastatur (Prueferfund, Okt 2026).
 * Haelt gerade ein Chat den Modus, bleibt er unangetastet.
 */
export const ensureNativeKeyboardResize = ({ platform, keyboard = Keyboard }) => {
  if (platform !== 'ios' || activeChats > 0) return;
  Promise.resolve(keyboard.setResizeMode({ mode: KeyboardResize.Native })).catch(() => {});
};

/** Zeit, die wir hoechstens auf das Schliessen einer offenen Tastatur warten (ms). */
const HIDE_TIMEOUT_MS = 700;

const useChatKeyboard = ({
  platform = Capacitor.getPlatform(),
  keyboard = Keyboard,
  win = typeof window !== 'undefined' ? window : undefined,
} = {}) => {
  const [state, setState] = useState(CLOSED);

  useEffect(() => {
    if (!win) return undefined;
    let disposed = false;
    const handles = [];
    const listen = (eventName, handler) => {
      Promise.resolve(keyboard.addListener(eventName, handler))
        .then((handle) => {
          if (disposed) handle?.remove?.();
          else handles.push(handle);
        })
        .catch(() => {});
    };

    if (platform === 'ios') {
      activeChats += 1;
      const switchToNone = () => {
        if (!disposed) Promise.resolve(keyboard.setResizeMode({ mode: KeyboardResize.None })).catch(() => {});
      };
      // Ist beim Betreten noch eine Tastatur offen (z.B. direkt aus der Suche),
      // ist die WebView gerade nativ verkleinert. Im Modus 'none' setzt das
      // Plugin sie beim Schliessen nicht mehr zurueck — unten bliebe ein Streifen
      // in Tastaturhoehe. Deshalb erst schliessen, dann umschalten.
      // (body.keyboard-open setzt utils/keyboardReveal.)
      const doc = win.document;
      if (doc && doc.body && doc.body.classList.contains('keyboard-open') && typeof keyboard.hide === 'function') {
        let switched = false;
        const switchOnce = () => {
          if (switched) return;
          switched = true;
          switchToNone();
        };
        listen('keyboardDidHide', switchOnce);
        setTimeout(switchOnce, HIDE_TIMEOUT_MS);
        Promise.resolve(keyboard.hide()).catch(switchOnce);
      } else {
        switchToNone();
      }
      listen('keyboardWillShow', (info) => {
        const height = Math.max(0, Math.round((info && info.keyboardHeight) || 0));
        setState({ open: height > 0, inset: height, animate: true, viewport: null });
      });
      listen('keyboardWillHide', () => setState({ ...CLOSED, animate: true }));
    } else if (platform === 'android') {
      // willShow kommt zu Beginn der Animation, zusammen mit dem Verkleinern
      // der WebView; willHide ebenso — so kehrt der Abstand gleichzeitig zurueck.
      listen('keyboardWillShow', () => setState({ ...CLOSED, open: true }));
      listen('keyboardWillHide', () => setState(CLOSED));
    } else if (win.visualViewport) {
      const vv = win.visualViewport;
      const update = () => {
        const viewport = readVisualViewport(win);
        setState((prev) => {
          if (!viewport) return prev.open ? CLOSED : prev;
          if (prev.viewport && prev.viewport.height === viewport.height && prev.viewport.top === viewport.top) {
            return prev;
          }
          return { open: true, inset: 0, animate: false, viewport };
        });
      };
      update();
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
      return () => {
        disposed = true;
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      };
    }

    return () => {
      disposed = true;
      handles.forEach((handle) => handle?.remove?.());
      if (platform === 'ios') {
        activeChats = Math.max(0, activeChats - 1);
        Promise.resolve(keyboard.setResizeMode({ mode: KeyboardResize.Native })).catch(() => {});
      }
    };
  }, [platform, keyboard, win]);

  return state;
};

export default useChatKeyboard;
